import React, { useState } from 'react';
import Tooltip from './Tooltip';
import { FieldCoordinates } from '../types/codeGeneration';
import { AccessibilityReport } from '../types/accessibilityReport';
import AccessibilityReportCard from './AccessibilityReportCard';

interface UploadResponse {
  documentId: string;
  originalFilename: string;
  fileSize: number;
  status: string;
  // Non-fatal things the ingest pipeline found in the author's own file. Absent, not
  // empty, when there is nothing to say.
  warnings?: Array<{ code: string; message: string }>;
  // The read-only accessibility scan's report card (ACCESSIBILITY_AUDIT_ON_UPLOAD.md).
  // Absent when the scan could not run; the card then simply does not render.
  accessibilityReport?: AccessibilityReport;
}

interface UploadSectionProps {
  selectedFile: File | null;
  uploadResult: UploadResponse;
  isUploading: boolean;
  error: string | null;
  selectedFields: FieldCoordinates[];
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  onBack: () => void;
  onDownloadOriginal: () => void;
}

const UploadSection: React.FC<UploadSectionProps> = ({
  selectedFile,
  uploadResult,
  isUploading,
  error,
  selectedFields,
  onFileSelect,
  onUpload,
  onBack,
  onDownloadOriginal
}) => {
  // One-time first-visit tip, replacing the old WelcomeTour modal — that
  // component fired on page mount regardless of auth state and targeted
  // this exact .upload-section class before it existed yet (pre-login,
  // pre-workflow-selection), so it never actually highlighted anything.
  // Reuses the same 'tourCompleted' localStorage flag for continuity.
  const [showFirstVisitTip, setShowFirstVisitTip] = useState(
    () => !localStorage.getItem('tourCompleted')
  );
  const dismissFirstVisitTip = () => {
    setShowFirstVisitTip(false);
    localStorage.setItem('tourCompleted', 'true');
  };

  return (
    <div className="max-w-4xl mx-auto mb-8 upload-section">
      <div className="bg-white rounded-2xl shadow-soft-lg p-8 border border-neutral-200 hover:shadow-soft-xl transition-shadow duration-300">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={onBack}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
                title="Back to workflow choice"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-2xl font-display font-semibold text-neutral-900">
                Upload Your PDF Form
              </h2>
            </div>
            <p className="text-sm text-neutral-500 ml-8">
              Select a PDF file to transform into a Spring Boot application
            </p>
          </div>
          <div className="hidden sm:block">
            <div className="relative">
              <div className="absolute inset-0 bg-primary-500/20 rounded-xl blur-xl"></div>
              <svg
                className="relative h-12 w-12 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
          </div>
        </div>

        {showFirstVisitTip && (
          <div className="flex items-start gap-3 bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6">
            <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 text-sm text-primary-900">
              <p>
                Upload a PDF below, then drag on it to draw form fields, customize their
                properties, and generate a ready-to-run Spring Boot project. To move around
                the PDF without creating fields, switch the toolbar to Pan or hold Space
                while dragging — and zoom with Ctrl+scroll or the −/+ buttons. Click the
                Help button in the header anytime for a detailed guide.
              </p>
            </div>
            <button
              type="button"
              onClick={dismissFirstVisitTip}
              className="text-primary-400 hover:text-primary-600 flex-shrink-0"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="space-y-4">
          {/* File Input - Drag & Drop Style */}
          <div className="relative file-upload-area">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                Choose PDF File or Drag & Drop
                <Tooltip content="Supports PDF files up to 25MB. The PDF should contain form fields or areas where you want to create input fields. Password-protected PDFs are not accepted — if yours carries one, save an unprotected copy and upload that." placement="right">
                  <svg className="w-4 h-4 ml-2 text-neutral-400 hover:text-primary-600 cursor-help transition-colors" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </Tooltip>
              </span>
            </label>
            <div className="relative group">
              <input
                type="file"
                accept=".pdf"
                onChange={onFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="border-2 border-dashed border-primary-300 rounded-xl p-8 text-center hover:border-primary-500 hover:bg-primary-50/50 transition-all duration-200 group-hover:scale-[1.02]">
                <svg
                  className="mx-auto h-16 w-16 text-primary-400 mb-4 group-hover:text-primary-600 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
                <p className="text-base font-medium text-neutral-700 mb-1">
                  Drop your PDF file here
                </p>
                <p className="text-sm text-neutral-500">
                  or click to browse from your computer
                </p>
                <p className="text-xs text-neutral-400 mt-2">
                  Supports PDF files up to 25MB
                </p>
                {/*
                  Stated BEFORE the upload, not after it fails. A password-protected file
                  is refused by PDFSanitizationService, and finding that out by being
                  rejected is a worse experience than being told in advance.

                  The second line is the reason, and it is the honest one: we do not want
                  the customer's document password. Framing the rule as something we
                  decline to hold, rather than something we cannot handle, is both more
                  accurate and more reassuring to a government buyer.
                */}
                <p className="text-xs text-neutral-500 mt-1 font-medium">
                  Password-protected PDFs are not accepted
                </p>
                <p className="text-xs text-neutral-400">
                  We never ask for a document password &mdash; keep it inside your organisation
                </p>
              </div>
            </div>
          </div>

          {/* Selected File Info & Upload Button */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {selectedFile && (
                <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-xl p-4 border border-primary-200 animate-slide-up">
                  <div className="flex items-center">
                    <svg className="h-10 w-10 text-primary-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-neutral-800">
                        {selectedFile.name.replace(/\.pdf\.pdf$/i, '.pdf')}
                      </p>
                      <p className="text-xs text-neutral-600">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • Ready to upload
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Upload Button */}
            <button
              onClick={onUpload}
              disabled={!selectedFile || isUploading}
              className={`ml-4 px-8 py-3.5 rounded-xl font-semibold transition-all duration-200 transform ${
                !selectedFile || isUploading
                  ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 hover:shadow-glow-md hover:-translate-y-0.5 active:translate-y-0'
              }`}
            >
              {isUploading ? (
                <div className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload PDF
                </div>
              )}
            </button>
          </div>

          {/* Status Messages */}
          <div className="space-y-4">
            {error && (
              <div className="bg-error-50 border border-error-200 rounded-xl p-4 animate-slide-down">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-error-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-error-800">Upload Failed</h3>
                    <p className="text-sm text-error-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Non-fatal warnings about the uploaded file.
              *
              * Sits between the failure panel and the success panel on purpose. The
              * upload DID succeed, so this must not look like the red "Upload Failed"
              * box; but the author has to see it before they start drawing fields,
              * because the only cheap remedies — editing the source document, or
              * deciding to publish the original alongside the form — are available to
              * them now and expensive later. Amber, not red, and it states what the
              * reader will experience rather than naming a standard. */}
            {uploadResult && uploadResult.documentId
              && uploadResult.warnings && uploadResult.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 animate-slide-down">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.63-1.516 2.63H3.72c-1.347 0-2.189-1.463-1.515-2.63L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Something to check before you build
                    </h3>
                    {uploadResult.warnings.map(warning => (
                      <p key={warning.code} className="text-sm text-amber-700 mt-1">
                        {warning.message}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {uploadResult && uploadResult.documentId && (
              <div className="bg-gradient-to-r from-success-50 to-success-100/50 border border-success-200 rounded-xl p-4 animate-slide-down">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-0 bg-success-500/20 rounded-full blur-xl animate-pulse-soft"></div>
                      <svg className="relative h-6 w-6 text-success-600" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-semibold text-success-800">Upload Complete!</h3>
                        <div className="text-sm text-success-700 mt-1">
                          <span className="font-medium">{uploadResult.originalFilename}</span>
                          <span className="text-success-600 text-xs block mt-1">
                            Document ID: {uploadResult.documentId}
                          </span>
                          {selectedFields.length === 0 && (
                            <span className="text-success-800 text-xs block mt-2 font-medium">
                              Next: drag on the PDF below to add your first field.
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={onDownloadOriginal}
                          className="inline-flex items-center gap-1 text-xs text-success-700 hover:text-success-900 underline mt-2"
                          title="Download the original PDF exactly as uploaded"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                          </svg>
                          Download Original PDF
                        </button>
                      </div>
                      {selectedFields.length > 0 && (
                        <div className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-semibold animate-scale-up">
                          {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* The accessibility report card (ACCESSIBILITY_AUDIT_ON_UPLOAD.md). Rendered after
              * the success panel because it describes the file that was just accepted: the author
              * reads where their form stands before they start building. Shown only when the scan
              * actually produced a report — a session load carries none, and a scan that could not
              * run simply omits it, so the card is absent rather than empty in both cases. */}
            {uploadResult && uploadResult.documentId && uploadResult.accessibilityReport && (
              <AccessibilityReportCard
                report={uploadResult.accessibilityReport}
                filename={uploadResult.originalFilename}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadSection;